import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckboxBlocked, CheckboxIndeterminate } from '../icons/Checkbox';
import { cn } from '../utils';

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  blocked?: boolean;
  iconHeight?: number;
  iconWidth?: number;
  plus?: boolean;
  dot?: boolean;
  highlight?: boolean;
  indeterminate?: boolean;
  outlined?: boolean;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      className,
      blocked,
      plus,
      highlight,
      indeterminate,
      dot,
      outlined,
      ...props
    },
    ref
  ) => {
    const shouldShowBlocked = blocked && !props.checked;
    const shouldShowPlus = plus && !props.checked;
    const shouldShowDot = dot && !!props.checked;

    return (
      <CheckboxPrimitive.Root
        {...props}
        ref={ref}
        checked={indeterminate ? 'indeterminate' : !!props.checked}
        disabled={blocked || props.disabled}
        className={cn(
          'peer relative h-6 w-6 shrink-0 rounded-sm focus-visible:outline-hidden',
          'data-[state=checked]:border-secondary data-[state=checked]:bg-secondary cursor-pointer hover:bg-transparent hover:opacity-100 data-[state=checked]:text-white',
          'data-[state=indeterminate]:border-secondary data-[state=indeterminate]:bg-secondary',
          {
            'border-primary border-2': !plus,
          },
          (outlined || dot) &&
            'data-[state=checked]:border-primary data-[state=checked]:text-primary data-[state=checked]:bg-white',
          'data-[state=unchecked]:disabled:border-disabled data-[state=checked]:disabled:border-disabled data-[state=checked]:disabled:bg-disabled disabled:cursor-not-allowed',
          className
        )}
      >
        {shouldShowDot && (
          <span className="absolute top-[3px] left-[3px] h-[calc(100%-6px)] w-[calc(100%-6px)] rounded-xs bg-current" />
        )}
        {shouldShowBlocked && <CheckboxBlocked className="text-primary" />}
        {shouldShowPlus && (
          <span className="group/check text-secondary flex h-10 w-10 items-center justify-center rounded transition-colors duration-200">
            <svg
              className="group-hover:hidden"
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
            >
              <path
                d="M19.0909 31.2987H20.9091V20.9091H31.2987V19.0909H20.9091V8.7013H19.0909V19.0909H8.7013V20.9091H19.0909V31.2987ZM3.8961 40C2.77056 40 1.83983 39.632 1.1039 38.8961C0.367965 38.1602 0 37.2294 0 36.1039V3.8961C0 2.77056 0.367965 1.83983 1.1039 1.1039C1.83983 0.367965 2.77056 0 3.8961 0H36.1039C37.2294 0 38.1602 0.367965 38.8961 1.1039C39.632 1.83983 40 2.77056 40 3.8961V36.1039C40 37.2294 39.632 38.1602 38.8961 38.8961C38.1602 39.632 37.2294 40 36.1039 40H3.8961ZM3.8961 38.1818H36.1039C36.6234 38.1818 37.0996 37.9654 37.5325 37.5325C37.9654 37.0996 38.1818 36.6234 38.1818 36.1039V3.8961C38.1818 3.37662 37.9654 2.90043 37.5325 2.46753C37.0996 2.03463 36.6234 1.81818 36.1039 1.81818H3.8961C3.37662 1.81818 2.90043 2.03463 2.46753 2.46753C2.03463 2.90043 1.81818 3.37662 1.81818 3.8961V36.1039C1.81818 36.6234 2.03463 37.0996 2.46753 37.5325C2.90043 37.9654 3.37662 38.1818 3.8961 38.1818Z"
                fill={highlight ? '#2FCFD3' : '#E2E8F0'}
              />
            </svg>
            <svg
              className="hidden group-hover:block group-hover/check:hidden"
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
            >
              <path
                d="M18.75 31.25H21.25V21.25H31.25V18.75H21.25V8.75H18.75V18.75H8.75V21.25H18.75V31.25ZM4.03875 40C2.88792 40 1.92708 39.6146 1.15625 38.8438C0.385417 38.0729 0 37.1121 0 35.9613V4.03875C0 2.88792 0.385417 1.92708 1.15625 1.15625C1.92708 0.385417 2.88792 0 4.03875 0H35.9613C37.1121 0 38.0729 0.385417 38.8438 1.15625C39.6146 1.92708 40 2.88792 40 4.03875V35.9613C40 37.1121 39.6146 38.0729 38.8438 38.8438C38.0729 39.6146 37.1121 40 35.9613 40H4.03875ZM4.03875 37.5H35.9613C36.3462 37.5 36.699 37.3398 37.0194 37.0194C37.3398 36.699 37.5 36.3462 37.5 35.9613V4.03875C37.5 3.65375 37.3398 3.30104 37.0194 2.98063C36.699 2.66021 36.3462 2.5 35.9613 2.5H4.03875C3.65375 2.5 3.30104 2.66021 2.98063 2.98063C2.66021 3.30104 2.5 3.65375 2.5 4.03875V35.9613C2.5 36.3462 2.66021 36.699 2.98063 37.0194C3.30104 37.3398 3.65375 37.5 4.03875 37.5Z"
                fill="currentColor"
              />
            </svg>
            <svg
              className="hidden group-hover/check:block"
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.75 31.25H21.25V21.25H31.25V18.75H21.25V8.75H18.75V18.75H8.75V21.25H18.75V31.25ZM4.03875 40C2.88792 40 1.92708 39.6146 1.15625 38.8438C0.385417 38.0729 0 37.1121 0 35.9613V4.03875C0 2.88792 0.385417 1.92708 1.15625 1.15625C1.92708 0.385417 2.88792 0 4.03875 0H35.9613C37.1121 0 38.0729 0.385417 38.8438 1.15625C39.6146 1.92708 40 2.88792 40 4.03875V35.9613C40 37.1121 39.6146 38.0729 38.8438 38.8438C38.0729 39.6146 37.1121 40 35.9613 40H4.03875Z"
                fill="currentColor"
              />
            </svg>
          </span>
        )}

        <CheckboxPrimitive.Indicator
          className={cn('flex items-center justify-center text-current')}
        >
          {indeterminate ? (
            <CheckboxIndeterminate className="text-[#2FCFD3]" />
          ) : plus ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
            >
              <path
                d="M4.03875 40C2.88792 40 1.92708 39.6146 1.15625 38.8438C0.385417 38.0729 0 37.1121 0 35.9613V4.03875C0 2.88792 0.385417 1.92708 1.15625 1.15625C1.92708 0.385417 2.88792 0 4.03875 0H35.9613C37.1121 0 38.0729 0.385417 38.8438 1.15625C39.6146 1.92708 40 2.88792 40 4.03875V35.9613C40 37.1121 39.6146 38.0729 38.8438 38.8438C38.0729 39.6146 37.1121 40 35.9613 40H4.03875Z"
                fill="#2FCFD3"
              />
              <path
                d="M16.5 28.7694L32.4906 12.7788L30.7212 11.0094L16.5 25.2306L9.375 18.1056L7.60563 19.875L16.5 28.7694Z"
                fill="white"
              />
            </svg>
          ) : (
            <svg
              width="13"
              height="10"
              viewBox="0 0 13 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.88675 9.38725L0 4.5005L1.864 2.6365L4.88675 5.65925L10.546 0L12.41 1.864L4.88675 9.38725Z"
                fill="currentColor"
              />
            </svg>
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
  }
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
